import { AnalysisResult } from "../contracts/scoring";
import { METRIC_GOVERNANCE_POLICY } from "../services/tabUnlockPolicy";

/**
 * GSC UI DATA CONTRACT - NO=100% (V3.2)
 * - Un onglet peut être visible MAIS doit être verrouillé si preuve DSP absente.
 * - ZÉRO fallback UI : aucune reconstruction, aucun score dérivé côté UI.
 *
 * IMPORTANT: IDs alignés avec viewPolicy + TabRouter:
 * - FINESSE tabId = "synchronization"
 */

export type LockReason =
  | "LEVEL_LOCKED"
  | "CALIBRATION_REQUIRED"
  | "MISSING_DSP_METRICS"
  | null;

function hasArr(a: unknown, minLen = 1): boolean {
  return Array.isArray(a) && a.length >= minLen;
}

function hasNum(n: unknown): boolean {
  return typeof n === "number" && Number.isFinite(n);
}

/**
 * Vérifie présence des preuves DSP nécessaires (NO=100%).
 * Si contrat inconnu => considéré manquant (lock).
 */
export const UI_METRIC_CONTRACT = {
  report: (r: AnalysisResult) => Boolean(r && r.session),

  /**
   * RHYTHM / MATCH (tabId = "rhythm")
   * Preuves DSP acceptées (sans reconstruction UI) :
   * - rhythm.stuPeaks (>=4) OU
   * - rhythm.stuEnvelope (>=8) OU
   * - debug.onsetTimesMs (>=4)
   */
  rhythm: (r: AnalysisResult) => {
    const rr: any = (r as any)?.rhythm;
    const dbg: any = r.session?.metrics?.debug;
    return (
      hasArr(rr?.stuPeaks, 4) ||
      hasArr(rr?.stuEnvelope, 8) ||
      hasArr(dbg?.onsetTimesMs, 4)
    );
  },

  // alias si certains endroits utilisent "match"
  match: (r: AnalysisResult) => (UI_METRIC_CONTRACT as any).rhythm(r),

  /**
   * HARMONY (tabId = "harmony")
   * Preuve: harmony.gridValidation
   */
  harmony: (r: AnalysisResult) => {
    const grid = (r as any)?.harmony?.gridValidation;
    return hasArr(grid, 1);
  },

  /**
   * FINESSE (tabId = "synchronization")
   * NO=100% strict (aligné avec ton FinesseTab) :
   * - dbg.stability
   * - dbg.timingDeviationMs (>=4)
   * - rhythm.stuPeaks (>=4) ✅ (pas de reconstruct depuis onsets)
   */
  synchronization: (r: AnalysisResult) => {
    const dbg: any = r.session?.metrics?.debug;
    const peaksStu = (r as any)?.rhythm?.stuPeaks;
    return (
      Boolean(dbg) &&
      hasNum(dbg.stability) &&
      hasArr(dbg.timingDeviationMs, 4) &&
      hasArr(peaksStu, 4)
    );
  },

  // alias rétro-compat éventuel
  finesse: (r: AnalysisResult) => (UI_METRIC_CONTRACT as any).synchronization(r),

  /**
   * NUANCE (tabId = "nuance")
   * Preuve: dbg.nuance.measurable + dbg.nuance.relativeDynamics
   */
  nuance: (r: AnalysisResult) => {
    const dbg: any = r.session?.metrics?.debug;
    const n = dbg?.nuance;
    return Boolean(n) && n.measurable === true && hasNum(n.relativeDynamics);
  },

  /**
   * CLEANLINESS (tabId = "cleanliness")
   * Preuve robuste: présence du champ tone.ghostNotes (même vide).
   */
  cleanliness: (r: AnalysisResult) => {
    const ghosts = (r as any)?.tone?.ghostNotes;
    return Array.isArray(ghosts);
  },

  /**
   * ARTICULATION / ATTAQUE (tabId = "articulation")
   * Preuves acceptées:
   * - session.metrics.articulation
   * - OU dbg.transient_ratio (preuve DSP directe)
   */
  articulation: (r: AnalysisResult) => {
    const m: any = r.session?.metrics;
    const dbg: any = r.session?.metrics?.debug;
    return (Boolean(m) && hasNum(m.articulation)) || hasNum(dbg?.transient_ratio);
  },

  /**
   * TONE / TIMBRE (tabId = "tone")
   * IMPORTANT: ton snapshot montre mfccData vide, MAIS tone.details + metrics.tone existent.
   * Donc preuve DSP = (tone.details présent) + (metrics.tone numérique)
   * -> NO=100% respecté : c’est un output d’analyse, pas un fallback UI.
   */
  tone: (r: AnalysisResult) => {
    const t: any = (r as any)?.tone;
    const m: any = r.session?.metrics;
    return Boolean(t?.details) && Boolean(m) && hasNum(m.tone);
  },

  /**
   * POSTURE (tabId = "posture")
   * Preuves acceptées :
   * - metrics.posture
   * - OU biomechanics présent (Vision pipeline)
   */
  posture: (r: AnalysisResult) => {
    const m: any = r.session?.metrics;
    const bio = (r as any)?.biomechanics;
    return (Boolean(m) && hasNum(m.posture)) || (bio !== null && bio !== undefined);
  },

  /**
   * AUDIO LAB (tabId = "audio")
   * Preuves acceptées :
   * - session.quality_score
   * - OU dbg.rms_db
   */
  audio: (r: AnalysisResult) => {
    const dbg: any = r.session?.metrics?.debug;
    return hasNum((r as any)?.session?.quality_score) || hasNum(dbg?.rms_db);
  },
} as const;

export function hasRequiredMetrics(
  analysis: AnalysisResult | null,
  tabId: string
): boolean {
  if (!analysis) return false;
  const validator = (UI_METRIC_CONTRACT as any)[tabId];
  if (typeof validator !== "function") return false; // contrat inconnu => lock NO=100%
  return Boolean(validator(analysis));
}

/**
 * Règles de verrouillage :
 * 1) niveau
 * 2) calibration (LIVE only)
 * 3) data DSP absente (NO=100%)
 */
export function getLockReason(tabId: string, analysis: AnalysisResult): LockReason {
  const policy = (METRIC_GOVERNANCE_POLICY as any)?.[tabId];

  // Pas de policy : pas de lock niveau, mais lock data
  if (!policy) {
    return hasRequiredMetrics(analysis, tabId) ? null : "MISSING_DSP_METRICS";
  }

  // 1) Niveau pédagogique
  if (analysis.coaching_level < policy.minLevel) {
    return "LEVEL_LOCKED";
  }

  // 2) Calibration (LIVE only)
  const isLive = analysis.session?.truth_report?.tech_pack?.origin === "REAL_AUDIO";
  if (
    isLive &&
    policy.requiresCalibration &&
    analysis.session?.truth_report?.calibration_status !== "VALID"
  ) {
    return "CALIBRATION_REQUIRED";
  }

  // 3) Data DSP absente
  if (!hasRequiredMetrics(analysis, tabId)) {
    return "MISSING_DSP_METRICS";
  }

  return null;
}

export function assertTabDataOrThrow(analysis: AnalysisResult, tabId: string): void {
  const reason = getLockReason(tabId, analysis);
  if (reason) {
    console.warn(`[UI-POLICY][NO=100%] tab=${tabId} locked reason=${reason}`);
  }
}
